import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BadgeIndicator, Box, Button, FileStatusCard, Loader, Popover, PopoverContent, PopoverTrigger, Typography } from '@launchmetrics/lm-ui';

import { FileDownloadOutline } from '@launchmetrics/lm-icons';
import { ReactComponent as OnlineIcon } from 'vendor/svg/report/online-icon.svg';
import { ReactComponent as PowerpointIcon } from 'vendor/svg/report/powerpoint-icon.svg';

import { useStyles } from './styles';
import { clearErrorItems, DownloadItem, removeItem } from 'store/report/reportDownloader';
import { useTranslation } from 'react-i18next';

export const DownloadManager = () => {
	const dispatch = useDispatch();
	const downloadItems = useSelector((state: RootState) => state.report.reportDownloader.items);
	const { header, downloadManager } = useStyles();
	const [open, setOpen] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		if (downloadItems.length === 0) setOpen(false);
	}, [downloadItems]);

	useEffect(() => {
		const interval = setInterval(() => {
			dispatch({ type: 'reportDownloader/updateDownloadItems' });
		}, 1000);
		return () => clearInterval(interval);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const onRetryReport = useCallback((item: DownloadItem) => {
		dispatch(removeItem(item.jobId));
		dispatch({ type: 'report/exportReport', payload: item.reportConfiguration });
	}, [dispatch]);

	const togglePopover = () => {
		if (open) {
			dispatch(clearErrorItems());
			setOpen(false);
		}
		else setOpen(true);
	};

	const renderItem = useCallback((item: DownloadItem) => {
		let fileCardParams: any;
		if (item.status === 'active') {
			fileCardParams = {
				...fileCardParams,
				status: 'in-progress',
				progressBar: {
					value: item.progress || 0,
					loading: true,
					hint: t("focus_page.table.text.loading")
				}
			};
		}
		else {
			fileCardParams = {
				...fileCardParams,
				status: 'error',
				errorText: t("reports.download.item.error"),
				onTryAgain: () => onRetryReport(item),
				tryAgain: t("reports.download.try_again")
			};
		}

		return <FileStatusCard
			id={`download-manager-file-card-${item.jobId}`}
			key={item.jobId}
			icon={item.reportConfiguration.type === 'online' ? <OnlineIcon style={{opacity: '25%'}} /> : <PowerpointIcon style={{opacity: '25%'}} />}
			title={item.reportConfiguration.name || "Unititled"}
			styleProps={downloadManager.fileCard}
			{...fileCardParams}
		/>;
	}, [t, onRetryReport, downloadManager]);

	const downloadIcon = useMemo(() => {
		const failed = downloadItems.some(item => item.status === 'failed');
		if (failed) return (
			<BadgeIndicator size='lg'>
				<FileDownloadOutline styleProps={header.fileIcon}/>
			</BadgeIndicator>
		);
		return (
			<>
				<Loader size={32} styleProps={header.loader}/>
				<FileDownloadOutline styleProps={header.fileIcon}/>
			</>
		);

	}, [downloadItems, header]);

	return downloadItems.length > 0
		? <Popover open={open} onOpenChange={() => {}} placement='bottom-start'>
			<PopoverTrigger onClick={togglePopover}>
				<Box styleProps={open ? {...header.triggerWrapper, ...header.triggerOpened} : {...header.triggerWrapper, ...header.triggerWrapper.hover}}>
					{downloadIcon}
				</Box>
			</PopoverTrigger>

			<PopoverContent id='download-manager-dialog' styleProps={downloadManager.main}>
				<Box>
					<Typography styleProps={downloadManager.header.title}>
						{t("reports.download.title")}
					</Typography>
				</Box>
				<Box>
					<Typography styleProps={downloadManager.header.description}>
						{t("reports.download.description")}
					</Typography>
				</Box>

				{downloadItems.map(renderItem)}

				<Box styleProps={downloadManager.closeBtn}>
					<Button onClick={togglePopover} variant='primary'>{t("tags.automated_tag_info.close")}</Button>
				</Box>

			</PopoverContent>
		</Popover>
		: null;
};
